import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';

import {
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';

import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { DynamicFormService } from 'src/app/services/dynamic-form.service';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.css'],
})

export class DynamicFormComponent implements OnInit, OnChanges {
  @Input() formConfig: any;
  @Output() formValueChanged = new EventEmitter<{ name: string; value: any }>();
  @Output() formControlTouched = new EventEmitter<{
    name: string;
    touched: boolean;
  }>();
  private destroy$ = new Subject<void>();

  dynamicForm: FormGroup;
  showMore: boolean = false;
  showMoreClicked: boolean = false;
  valueInput: string;

  arrayValueInput = [];
  previousValue = {};

  constructor(
    private fb: FormBuilder,
    private dynamicFormService: DynamicFormService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.createForm(this.formConfig);
    this.dynamicFormService.setFormGroup(this.dynamicForm);
    this.dynamicFormService
      .getFormSubmitObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dynamicForm.updateValueAndValidity({
          onlySelf: false,
          emitEvent: true,
        });
        this.onSubmit();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.formConfig && !changes.formConfig.firstChange) {
      this.createForm(this.formConfig);
      this.dynamicFormService.setFormGroup(this.dynamicForm);
    }

  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBlur(field) {
    setTimeout(() => {
      const control = this.dynamicForm.get(field.name);
      if (control.touched) {
        this.formControlTouched.emit({
          name: field.name,
          touched: control.touched,
        });
      }
    }, 0);
  }

  createForm(config) {
    const group = {};
    config.forEach((field) => {
      const control = new FormControl(
        field.value,
        this.bindValidations(field.validations || [])
      );

      group[field.name] = control;
      control.valueChanges.subscribe((value) => {
        this.formValueChanged.emit({ name: field.name, value });
      });
    });
    this.dynamicForm = this.fb.group(group);

    config.forEach((field) => {
      if (field.showIf) {
        this.dynamicForm
          ?.get(field.showIf.fieldName)
          ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe((input) => {
            if (this.valueInput == input) {
              return;
            }
            this.valueInput = input;
            this.updateDependentValidations(field.showIf.fieldName, input);
          });
        this.handleDependency(field); // Initial check to set visibility
      } else if (field.showElse) {
        this.dynamicForm
          ?.get(field.showElse.fieldName)
          ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe((input) => {
            if (this.valueInput == input) {
              return;
            }
            this.valueInput = input;
            this.updateDependentValidations(field.showElse.fieldName, input);
          });
        this.handleDependency(field); // Initial check to set visibility
      }
      if (field.type === 'chip-list') {
        this.previousValue[field.name] = field.value;
        this.dynamicForm
          ?.get(field.name)
          ?.valueChanges.pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe((currentValue) => {
            if (this.arrayValueInput && this.arrayValueInput.length && currentValue && currentValue.length && this.arraysExactMatch(this.arrayValueInput, currentValue)) {
              return;
            }
            this.arrayValueInput = currentValue;
            this.selectItem(field.name);
            this.previousValue[field.name] = currentValue;
          });
      }
    });
  }

  arraysExactMatch(arr1: any[], arr2: any[]): boolean {
    if (arr1.length !== arr2.length) return false;

    return arr1.every((value, index) => value === arr2[index]);
  }

  bindValidations(validations) {
    if (validations.length > 0) {
      const validList = [];
      validations.forEach((valid) => {
        if (valid.name === 'required') {
          validList.push(Validators.required);
        } else if (valid.name === 'maxlength') {
          validList.push(Validators.maxLength(valid.value));
        } else if (valid.name === 'regex') {
          validList.push(this.regexValidator(valid.value));
        }
        // Add other validators as needed
      });
      return Validators.compose(validList);
    }
    return null;
  }

  regexValidator(regexValue): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const regex = new RegExp(regexValue);
      let valid = true;
      if (control.value) {
        valid = regex.test(control.value);
        return valid ? null : { regex: true };
      } else return null;
    };
  }

  getErrorMessage(field) {
    const control = this.dynamicForm.get(field.name);
    if (control.hasError('required')) {
      if (field.label) return `${field.label} is required.`;
      else return `Value is required.`;
    } else if (control.hasError('maxlength')) {
      let limit = 3500;
      field.validations.forEach((element) => {
        if (element.name == 'maxlength') {
          limit = element.value;
        }
      });
      if (field.label)
        return `${field.label} must not exceed ${limit} characters.`;
      else return `Value must not exceed ${limit} characters.`;
    } else if (control.hasError('regex')) {
      let ele = {
        message: 'Please enter valid details',
      };
      field.validations.forEach((element) => {
        if (element.name == 'regex') {
          ele = element;
        }
      });
      return ele.message;
    }
    return '';
  }

  removeItem(fieldName: string, item: any): void {
    const control = this.dynamicForm.get(fieldName);
    if (control) {
      const currentValue = control.value || [];
      const index = currentValue.indexOf(item);

      if (index >= 0) {
        currentValue.splice(index, 1);
        control.setValue(currentValue);
      }
    }
  }

  selectItem(fieldName: string): void {

    const control = this.dynamicForm.get(fieldName);
    if (control) {
      const currentValue = control.value || [];

      if (currentValue.length > 0) {
        if (this.previousValue && this.previousValue[fieldName] && this.previousValue[fieldName].length == 1 && currentValue.length > 0 && this.previousValue[fieldName][0].split(' ')[0] == 'NO') {
          let value = currentValue.slice(1);
          control.setValue(value);
        } else if (currentValue[0].split(' ')[0] == 'NO') {
          control.setValue([currentValue[0]]);
        }
      }
    }
  }

  selectBedroom(fieldName: string, roomTitle: string): void {
    const control = this.dynamicForm.get(fieldName);
    if (control) {
      control.setValue(roomTitle);
    }
  }

  selectCard(controlName: string, value: string) {
    this.dynamicForm.get(controlName).setValue(value);
  }

  handleDependency(field: any) {
    const control = this.dynamicForm?.get(field.name);

    let shouldShow = false;
    if (field.showIf) {
      shouldShow = field.showIf.value.indexOf(
        this.dynamicForm?.get(field.showIf.fieldName)?.value
      ) >= 0;
    } else if (field.showElse) {
      shouldShow = field.showElse.value.indexOf(
        this.dynamicForm?.get(field.showElse.fieldName)?.value
      ) < 0;
    }

    if (shouldShow) {
      control?.setValidators(this.bindValidations(field.validations || []));
      control?.enable(); // Enable the control
    } else {
      control?.clearValidators(); // Clear validators when not shown
      control?.disable(); // Disable the control
      control?.reset(); // Reset the value
    }
  }

  updateDependentValidations(fieldName: string, optionValue: string) {
    this.formConfig.forEach((field) => {
      if (field.showIf && field.showIf.fieldName == fieldName) {
        const control = this.dynamicForm.get(field.name);
        if (field.showIf.value.indexOf(optionValue) >= 0) {
          // Apply validators and enable the field for the current option
          control?.setValidators(this.bindValidations(field.validations || []));
          control?.enable();
        } else {
          // Clear validators and disable the field for other options
          control?.clearValidators();
          control?.disable();
          control?.reset(); // Optional: Reset the field value to clear any existing data
        }
        control?.updateValueAndValidity(); // Ensure the form control is updated immediately
      } else if (field.showElse && field.showElse.fieldName == fieldName) {
        const control = this.dynamicForm.get(field.name);
        if (field.showElse.value.indexOf(optionValue) < 0) {
          // Apply validators and enable the field for the current option
          control?.setValidators(this.bindValidations(field.validations || []));
          control?.enable();
        } else {
          // Clear validators and disable the field for other options
          control?.clearValidators();
          control?.disable();
          control?.reset(); // Optional: Reset the field value to clear any existing data
        }
        control?.updateValueAndValidity(); // Ensure the form control is updated immediately
      }
    });
    this.dynamicForm.updateValueAndValidity({
      onlySelf: false,
      emitEvent: true,
    });
    this.cdr.detectChanges();
  }

  currencyValueChange(fieldName: string) {
    let value = this.dynamicForm?.get(fieldName)?.value;

    if (value) {
      // Ensure the value is a string before calling replace()
      let y = typeof value == 'string' ? value.replace(/,/g, '') : value;
      // Parse the value to an integer if it's still not a number
      let x = parseInt(y, 10); // Ensure the base is specified (base 10)
      const control = this.dynamicForm.get(fieldName);
      if (control) {
        control.setValue(x);
      }
    }
  }

  onSubmit() {
    this.cdr.detectChanges(); // Optional: Force change detection if necessary

    // Handle form submission if valid
    if (this.dynamicForm.valid) {
      this.formConfig.forEach((element) => {
        if (element.type == 'currency') {
          let value = this.dynamicForm?.get(element.name)?.value;

          if (value && typeof value == 'string') {
            // Ensure the value is a string before calling replace()
            let y = typeof value == 'string' ? value.replace(/,/g, '') : value;
            // Parse the value to an integer if it's still not a number
            let x = parseInt(y, 10); // Ensure the base is specified (base 10)
            const control = this.dynamicForm.get(element.name);
            if (control) {
              control.setValue(x);
            }
          }
        }
      });
      console.log('this.dynamicForm.value : ', this.dynamicForm.value);
      this.dynamicFormService.submitSuccess(this.dynamicForm.value);
    } else {
      // Mark all controls as touched to trigger validation messages in the UI
      this.dynamicForm.markAllAsTouched();
      // Log invalid controls and their respective errors
      Object.keys(this.dynamicForm.controls).forEach((controlName) => {
        const control = this.dynamicForm.get(controlName);
        if (control && control.invalid) {
          console.log(controlName, control.errors);
        }
      });

      // Trigger error handling in your form service
      this.dynamicFormService.submitError(this.dynamicForm.invalid);
    }
  }
}
